import {
  Card,
  CodeBlock,
  HStack,
  Heading,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

/**
 * "We benchmarked TimesFM zero-shot against Prophet."
 *
 * Every number in this file comes from benchmarks/results/ in the repository,
 * and the same rows are downloadable from /benchmarks/m4-300-per-series.csv.
 * Nothing here is rounded in our favour or quoted from the TimesFM paper: if a
 * figure changes, rerun the benchmark, refresh the published CSVs and update
 * both this page and benchmarks/README.md together.
 */

const LOCAL_RUN = `# The hosted path: this script, a free-tier key, nothing else.
# Create a key at https://vectime.cloud/app/keys — no card required.
curl -O https://vectime.cloud/benchmarks/run.py

VECTIME_API_KEY=vct_live_… uv run run.py \\
  --freq hourly weekly monthly --series 100 --seed 7`;

interface ScoreRow {
  freq: string;
  method: string;
  smape: string;
  mase: string;
  coverage: string;
  best?: boolean;
}

/** benchmarks/results/summary.csv, verbatim. */
const SCORES: ScoreRow[] = [
  { freq: 'Hourly', method: 'TimesFM (zero-shot)', smape: '9.095 / 5.225', mase: '0.741 / 0.566', coverage: '82.5%', best: true },
  { freq: 'Hourly', method: 'seasonal-naive', smape: '16.146 / 6.854', mase: '1.277 / 1.204', coverage: '—' },
  { freq: 'Hourly', method: 'Prophet', smape: '21.233 / 12.672', mase: '1.840 / 1.255', coverage: '65.0%' },
  { freq: 'Weekly', method: 'TimesFM (zero-shot)', smape: '6.297 / 3.779', mase: '1.905 / 1.152', coverage: '86.8%', best: true },
  { freq: 'Weekly', method: 'seasonal-naive', smape: '9.544 / 5.256', mase: '2.569 / 1.830', coverage: '—' },
  { freq: 'Weekly', method: 'Prophet', smape: '21.019 / 11.253', mase: '7.012 / 2.538', coverage: '58.5%' },
  { freq: 'Monthly', method: 'TimesFM (zero-shot)', smape: '15.074 / 8.714', mase: '1.017 / 0.793', coverage: '75.8%', best: true },
  { freq: 'Monthly', method: 'seasonal-naive', smape: '16.781 / 10.357', mase: '1.224 / 0.990', coverage: '—' },
  { freq: 'Monthly', method: 'Prophet', smape: '23.661 / 11.244', mase: '1.349 / 1.085', coverage: '44.8%' },
];

/** Per-series MASE comparisons, out of 100 series per frequency. */
const LOSSES = [
  { freq: 'Hourly', naive: '20 / 100', prophet: '8 / 100', clean: '78 / 100' },
  { freq: 'Weekly', naive: '32 / 100', prophet: '17 / 100', clean: '59 / 100' },
  { freq: 'Monthly', naive: '34 / 100', prophet: '34 / 100', clean: '48 / 100' },
];

interface CoverageGroup {
  freq: string;
  timesfm: number;
  prophet: number;
  /** Series, out of 100, whose own interval covered at least 80% of the truth. */
  timesfmSeries: number;
  prophetSeries: number;
}

const COVERAGE: CoverageGroup[] = [
  { freq: 'Hourly', timesfm: 0.825, prophet: 0.65, timesfmSeries: 62, prophetSeries: 25 },
  { freq: 'Weekly', timesfm: 0.868, prophet: 0.585, timesfmSeries: 75, prophetSeries: 39 },
  { freq: 'Monthly', timesfm: 0.758, prophet: 0.448, timesfmSeries: 53, prophetSeries: 21 },
];

/**
 * Coverage against the 80% both methods promised.
 *
 * Hand-rolled SVG for the same reason as ForecastChart: six bars and a rule do
 * not justify a charting dependency, and the tokens keep it correct in both
 * themes.
 */
function CoverageChart() {
  const width = 720;
  const height = 240;
  const pad = { top: 16, right: 16, bottom: 34, left: 44 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const y = (v: number) => pad.top + (1 - v) * plotH;
  const groupW = plotW / COVERAGE.length;
  const barW = 46;

  return (
    <VStack gap={1.5}>
      <div className="chart-wrap">
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Interval coverage by frequency: TimesFM 82.5, 86.8 and 75.8 percent against Prophet's 65.0, 58.5 and 44.8 percent, both promising 80 percent"
        >
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
            <g key={tick}>
              <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--color-border)" strokeWidth={1} />
              <text x={pad.left - 8} y={y(tick) + 4} textAnchor="end" fontSize={11} fill="var(--color-text-secondary)">
                {`${tick * 100}%`}
              </text>
            </g>
          ))}

          {/* What both methods promised. Every bar is read against this line. */}
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={y(0.8)}
            y2={y(0.8)}
            stroke="var(--color-border-emphasized)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
          <text x={width - pad.right} y={y(0.8) - 6} textAnchor="end" fontSize={11} fill="var(--color-text-secondary)">
            promised 80%
          </text>

          {COVERAGE.map((group, i) => {
            const centre = pad.left + groupW * i + groupW / 2;
            const bars = [
              { value: group.timesfm, fill: 'var(--color-icon-blue)', x: centre - barW - 6 },
              { value: group.prophet, fill: 'var(--color-icon-orange)', x: centre + 6 },
            ];
            return (
              <g key={group.freq}>
                {bars.map((bar) => (
                  <g key={bar.fill}>
                    <rect x={bar.x} y={y(bar.value)} width={barW} height={plotH - (y(bar.value) - pad.top)} fill={bar.fill} rx={3} />
                    <text x={bar.x + barW / 2} y={y(bar.value) - 6} textAnchor="middle" fontSize={11} fill="var(--color-text-secondary)">
                      {`${(bar.value * 100).toFixed(1)}%`}
                    </text>
                  </g>
                ))}
                <text x={centre} y={height - 10} textAnchor="middle" fontSize={12} fill="var(--color-text-secondary)">
                  {group.freq}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--color-icon-blue)' }} />
          TimesFM q10–q90
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--color-icon-orange)' }} />
          Prophet 80% interval
        </span>
      </div>
    </VStack>
  );
}

/**
 * Median sMAPE per frequency, from the same summary.csv the table above prints.
 *
 * Median rather than mean, because the mean is dragged around by a handful of
 * series with tiny denominators — the table shows both, the chart shows the one
 * that describes a typical series.
 */
const ERRORS = [
  { freq: 'Hourly', timesfm: 5.225, naive: 6.854, prophet: 12.672 },
  { freq: 'Weekly', timesfm: 3.779, naive: 5.256, prophet: 11.253 },
  { freq: 'Monthly', timesfm: 8.714, naive: 10.357, prophet: 11.244 },
];

/** Series out of 100, per frequency, on which TimesFM was beaten on MASE. */
const BEATEN = [
  { freq: 'Hourly', byNaive: 20, byProphet: 8 },
  { freq: 'Weekly', byNaive: 32, byProphet: 17 },
  { freq: 'Monthly', byNaive: 34, byProphet: 34 },
];

const SERIES_COLOURS = {
  timesfm: 'var(--color-icon-blue)',
  naive: 'var(--color-icon-gray)',
  prophet: 'var(--color-icon-orange)',
} as const;

interface GroupedBarsProps {
  /** One group per label, each with its bars in draw order. */
  groups: readonly { label: string; bars: readonly { value: number; fill: string }[] }[];
  /** Top of the value axis. Bars are drawn as a fraction of this. */
  max: number;
  /** Values to draw a gridline and a label at. */
  ticks: readonly number[];
  /** How an axis tick is written. */
  format: (value: number) => string;
  /**
   * How a bar's own label is written. Defaults to `format`. Split out because
   * a tick wants a round number and a measurement wants its precision — the
   * axis reads `10%` while the bar above it reads `12.7%`.
   */
  formatValue?: (value: number) => string;
  /** Sentence describing the whole chart for a screen reader. */
  label: string;
}

/**
 * Grouped bars, hand-rolled for the same reason CoverageChart is: three groups
 * and an axis do not justify a charting dependency, and the theme tokens keep
 * it legible in both light and dark without a second palette.
 */
function GroupedBars({ groups, max, ticks, format, formatValue = format, label }: GroupedBarsProps) {
  const width = 720;
  const height = 240;
  const pad = { top: 16, right: 16, bottom: 34, left: 46 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const y = (v: number) => pad.top + (1 - v / max) * plotH;
  const groupW = plotW / groups.length;
  const barW = 42;
  const gap = 8;

  return (
    <div className="chart-wrap">
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={label}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--color-border)" strokeWidth={1} />
            <text x={pad.left - 8} y={y(tick) + 4} textAnchor="end" fontSize={11} fill="var(--color-text-secondary)">
              {format(tick)}
            </text>
          </g>
        ))}

        {groups.map((group, i) => {
          const centre = pad.left + groupW * i + groupW / 2;
          // Bars are laid out from the group's centre so a group of two and a
          // group of three both stay centred under their label.
          const span = group.bars.length * barW + (group.bars.length - 1) * gap;
          const start = centre - span / 2;
          return (
            <g key={group.label}>
              {group.bars.map((bar, j) => {
                const x = start + j * (barW + gap);
                return (
                  <g key={bar.fill}>
                    <rect x={x} y={y(bar.value)} width={barW} height={plotH - (y(bar.value) - pad.top)} fill={bar.fill} rx={3} />
                    <text x={x + barW / 2} y={y(bar.value) - 6} textAnchor="middle" fontSize={11} fill="var(--color-text-secondary)">
                      {formatValue(bar.value)}
                    </text>
                  </g>
                );
              })}
              <text x={centre} y={height - 10} textAnchor="middle" fontSize={12} fill="var(--color-text-secondary)">
                {group.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** The swatch row under a chart. */
function Legend({ items }: { items: readonly { fill: string; label: string }[] }) {
  return (
    <div className="legend">
      {items.map((item) => (
        <span key={item.label} className="legend__item">
          <span className="legend__swatch" style={{ background: item.fill }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/** Median sMAPE, lower is better. The table's first column, drawn. */
function ErrorChart() {
  return (
    <VStack gap={1.5}>
      <GroupedBars
        max={15}
        ticks={[0, 5, 10, 15]}
        format={(v) => `${v}%`}
        formatValue={(v) => `${v.toFixed(1)}%`}
        label="Median sMAPE by frequency, lower is better. Hourly: TimesFM 5.2, seasonal-naive 6.9, Prophet 12.7. Weekly: TimesFM 3.8, seasonal-naive 5.3, Prophet 11.3. Monthly: TimesFM 8.7, seasonal-naive 10.4, Prophet 11.2."
        groups={ERRORS.map((row) => ({
          label: row.freq,
          bars: [
            { value: row.timesfm, fill: SERIES_COLOURS.timesfm },
            { value: row.naive, fill: SERIES_COLOURS.naive },
            { value: row.prophet, fill: SERIES_COLOURS.prophet },
          ],
        }))}
      />
      <Legend
        items={[
          { fill: SERIES_COLOURS.timesfm, label: 'TimesFM (zero-shot)' },
          { fill: SERIES_COLOURS.naive, label: 'seasonal-naive' },
          { fill: SERIES_COLOURS.prophet, label: 'Prophet (defaults)' },
        ]}
      />
    </VStack>
  );
}

/** How often the average win is not the win on your series. */
function LossChart() {
  return (
    <VStack gap={1.5}>
      <GroupedBars
        max={40}
        ticks={[0, 10, 20, 30, 40]}
        format={(v) => `${v}`}
        label="Series out of 100 on which TimesFM was beaten. Hourly: 20 by seasonal-naive, 8 by Prophet. Weekly: 32 by seasonal-naive, 17 by Prophet. Monthly: 34 by seasonal-naive, 34 by Prophet."
        groups={BEATEN.map((row) => ({
          label: row.freq,
          bars: [
            { value: row.byNaive, fill: SERIES_COLOURS.naive },
            { value: row.byProphet, fill: SERIES_COLOURS.prophet },
          ],
        }))}
      />
      <Legend
        items={[
          { fill: SERIES_COLOURS.naive, label: 'beaten by seasonal-naive' },
          { fill: SERIES_COLOURS.prophet, label: 'beaten by Prophet' },
        ]}
      />
    </VStack>
  );
}

export function TimesfmVsProphetArticle() {
  return (
    <VStack gap={4}>
      <VStack gap={2}>
        <Text size="lg">
          On 300 series from the M4 competition dataset, TimesFM 2.5 forecasting zero-shot — no
          fitting, no tuning, no per-series model — had a lower mean and median error than both
          Prophet at its defaults and seasonal-naive, at every frequency we tested. It also lost to
          seasonal-naive on 20, 32 and 34 series out of 100. The largest gap between the two methods
          was not point accuracy at all: asked for an 80% interval, Prophet delivered 44.8% coverage
          on Monthly data, and TimesFM delivered 75.8%.
        </Text>
        <Text color="secondary">
          Everything below is reproducible. The script, the summary and every per-series row are
          linked at the end, and the sections you would use to argue against us — the losses, the
          setup Prophet was denied, the metrics that disagree with each other — are in the middle
          rather than the footnotes.
        </Text>
      </VStack>

      <Card padding={3} variant="muted">
        <VStack gap={1.5}>
          <Text weight="semibold">A disclosure worth reading first</Text>
          <Text size="sm" color="secondary">
            We sell a hosted TimesFM API, so we are the last people whose benchmark you should take
            on trust. That is exactly why the per-series CSV is downloadable rather than summarised,
            why seasonal-naive — the baseline that is embarrassingly hard to beat — is in every
            table, and why the count of series where TimesFM lost is a section rather than a
            sentence. Disagree with it by rerunning it.
          </Text>
        </VStack>
      </Card>

      <VStack gap={2}>
        <Heading level={2} id="what-we-ran">What we ran</Heading>
        <Text color="secondary">
          <Link href="https://github.com/Mcompetitions/M4-methods" isExternalLink>
            M4
          </Link>{' '}
          is a public competition dataset with its own train/test split, its own horizons and its own
          seasonal periods. Those are not ours to choose: changing them would make these numbers
          incomparable to every published M4 result, which is most of what they are worth. We sampled
          100 series each from Hourly (horizon 48, seasonal period 24), Weekly (13, 1) and Monthly
          (18, 12) with a seeded RNG, so the same command draws the same series on your machine and
          ours.
        </Text>

        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Method</TableHeaderCell>
              <TableHeaderCell>How it was run</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <Text size="sm" weight="semibold">
                  TimesFM 2.5 (200M)
                </Text>
              </TableCell>
              <TableCell>
                <Text size="sm" color="secondary">
                  Zero-shot and batched, with no fitting step of any kind. Served over{' '}
                  <span className="mono">POST /v1/forecast</span> on a free-tier key, so history was
                  capped at the free plan&apos;s 1,024-point context window.
                </Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text size="sm" weight="semibold">
                  Prophet 1.1.6
                </Text>
              </TableCell>
              <TableCell>
                <Text size="sm" color="secondary">
                  Fitted per series, default settings, <span className="mono">interval_width=0.8</span>{' '}
                  to match the q10–q90 band. No holidays, no regressors — see the section on that
                  below, because it matters.
                </Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text size="sm" weight="semibold">
                  seasonal-naive
                </Text>
              </TableCell>
              <TableCell>
                <Text size="sm" color="secondary">
                  Repeat the last seasonal cycle. On Weekly, where M4 sets the seasonal period to 1,
                  this degenerates to &ldquo;next week equals this week&rdquo; and is still hard to beat.
                </Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Text color="secondary">
          Scores are sMAPE and MASE as M4 defines them, plus one M4 does not report:{' '}
          <strong>coverage</strong>, the share of held-out points that landed inside the interval each
          method promised. Prophet&apos;s default interval is 80% and the q10–q90 band is also 80%, so
          the comparison is like for like. Everything ran on an M1 Pro, on CPU.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="results">The results</Heading>
        <Text color="secondary">
          Lower is better for sMAPE and MASE, and both columns read as mean / median. For coverage,
          80% is the target rather than a maximum — above it is conservative, below it is
          overconfident.
        </Text>

        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Frequency</TableHeaderCell>
              <TableHeaderCell>Method</TableHeaderCell>
              <TableHeaderCell>sMAPE</TableHeaderCell>
              <TableHeaderCell>MASE</TableHeaderCell>
              <TableHeaderCell>Coverage</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SCORES.map((row) => (
              <TableRow key={`${row.freq}-${row.method}`}>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {row.freq}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text size="sm" weight={row.best ? 'semibold' : 'normal'}>
                    {row.method}
                  </Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.smape}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.mase}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.coverage}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ErrorChart />
        <Text size="sm" color="secondary">
          The same first column, drawn: median sMAPE per frequency, where a shorter bar is a better
          forecast. The gap that matters is not TimesFM against Prophet — it is how close plain
          seasonal-naive stays to both.
        </Text>

        <Text size="sm" color="secondary">
          sMAPE and MASE disagree often, and that is information rather than a bug. sMAPE is
          scale-free but penalises over- and under-forecasting asymmetrically; MASE compares against
          the seasonal-naive error on the training portion of the same series, so it answers
          &ldquo;is this better than the trivial method&rdquo; directly. Weekly is the clearest case: TimesFM
          has by far the best sMAPE at 6.297, and a MASE of 1.905 — better than both baselines, and
          still worse than the in-sample naive error on the average series.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="where-timesfm-loses">Where TimesFM loses</Heading>
        <Text color="secondary">
          A table of means is the least interesting way to read a benchmark, because a mean can win
          while losing on a third of the individual cases. Per series, by MASE — &ldquo;naive&rdquo; is
          seasonal-naive:
        </Text>

        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Frequency</TableHeaderCell>
              <TableHeaderCell>Lost to naive</TableHeaderCell>
              <TableHeaderCell>Lost to Prophet</TableHeaderCell>
              <TableHeaderCell>Beat both</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LOSSES.map((row) => (
              <TableRow key={row.freq}>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {row.freq}
                  </Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.naive}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.prophet}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.clean}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <LossChart />
        <Text size="sm" color="secondary">
          Out of 100 series per frequency. The averages above say TimesFM wins everywhere; this says
          that on a third of monthly series the thing you would have got for free beat it. Both are
          true, and only one of them is on most vendors&apos; comparison pages.
        </Text>

        <Text color="secondary">
          Monthly is the honest result here, and it is the frequency most business series actually
          live at. TimesFM is beaten by both baselines on 16 of 100 Monthly series, wins outright on
          48, and its mean MASE advantage over plain seasonal-naive is 1.017 against 1.224. That is a
          real improvement and it is not a large one. If your data is monthly, treat this as a reason
          to run the comparison on your own series rather than a reason to skip it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="intervals">The bigger gap is the interval, not the point</Heading>
        <Text color="secondary">
          Point error is what benchmarks report and it is rarely what breaks a plan. If you size
          inventory from a P90, or alert when a metric leaves its expected band, what you depend on
          is the interval meaning what it says. Both methods were asked for 80%.
        </Text>

        <CoverageChart />

        <Text color="secondary">
          Prophet was overconfident at every frequency, and on Monthly data fewer than half the
          actual values fell inside the range it promised. TimesFM was slightly wide on Hourly and
          Weekly, slightly optimistic on Monthly, and near nominal throughout. The same pattern holds
          series by series rather than only in the average, counting the series whose own interval
          covered at least 80% of the held-out points:
        </Text>

        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Frequency</TableHeaderCell>
              <TableHeaderCell>TimesFM ≥ 80%</TableHeaderCell>
              <TableHeaderCell>Prophet ≥ 80%</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {COVERAGE.map((row) => (
              <TableRow key={row.freq}>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {row.freq}
                  </Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.timesfmSeries} / 100</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{row.prophetSeries} / 100</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Text size="sm" color="secondary">
          Seasonal-naive has no interval at all, which is why its coverage cell is blank rather than
          zero. That is its own kind of answer: the cheapest method gives you a number and no way to
          size the risk around it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="prophet-mean">Prophet&apos;s mean is not Prophet&apos;s typical</Heading>
        <Text color="secondary">
          On Weekly data Prophet&apos;s mean MASE is 7.012 and its median is 2.538. The mean is dragged
          by a handful of catastrophic fits — the worst single series scored 158.6, against a worst
          case of 12.1 for TimesFM on the same set. Quoting only the mean would misrepresent Prophet
          as far worse than it usually is, which is why both columns are in the table above. The
          failure mode is real and worth knowing about, but it is a tail, not a typical case.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="when-prophet-wins">When Prophet is still the right call</Heading>
        <Text color="secondary">
          This benchmark ran Prophet in the setup that suits it least, and that is a limitation of
          the benchmark rather than a verdict on the tool.
        </Text>
        <VStack gap={1.5}>
          <Text color="secondary">
            <strong>You know something about the future.</strong> Prophet is designed to be handed
            holidays, promotions and other known future events as regressors. It was given none here.
            On a series driven by events you can enumerate, a Prophet model with those events
            supplied should be expected to do better than it does in this table — and TimesFM, in
            this setup, cannot use them at all.
          </Text>
          <Text color="secondary">
            <strong>You need the decomposition, not just the forecast.</strong> Prophet hands back
            separable trend, seasonality and holiday components. A foundation model hands back a
            distribution over what happens next and no account of why.
          </Text>
          <Text color="secondary">
            <strong>Your series is well served by something simpler.</strong> Seasonal-naive beat
            TimesFM on a third of Monthly series. Whatever you pick, benchmark it against repeating
            the last cycle before you believe it — on strongly periodic data that is a genuinely hard
            baseline, and it costs nothing to run.
          </Text>
        </VStack>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="what-would-move-these">What would move these numbers</Heading>
        <VStack gap={1.5}>
          <Text color="secondary">
            <strong>A longer context.</strong> The free tier caps history at 1,024 points. M4&apos;s
            Hourly series run to 960, so they fit, but a larger window would give the model more to
            work with on longer histories.
          </Text>
          <Text color="secondary">
            <strong>A tuned Prophet.</strong> These are defaults: no tuned seasonality, no holidays,
            no regressors, no cross-validated changepoint prior.
          </Text>
          <Text color="secondary">
            <strong>More series.</strong> 100 per frequency is enough to see the shape and not enough
            to split hairs. The script takes <span className="mono">--series 500</span>.
          </Text>
        </VStack>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="run-it-yourself">Run it yourself</Heading>
        <Text color="secondary">
          The script downloads the M4 files itself and{' '}
          <Link href="https://docs.astral.sh/uv/" isExternalLink>
            uv
          </Link>{' '}
          installs the dependencies from the pins in its header. Those pins are load-bearing:
          cmdstanpy 1.3 breaks Prophet 1.1.6&apos;s backend loader and pandas 3 breaks its internal
          reindexing, both per series and both caught, so without the pins Prophet quietly becomes a
          column of <span className="mono">NaN</span> — a benchmark in which the competitor never ran
          and which looks like a clean sweep.
        </Text>

        <CodeBlock code={LOCAL_RUN} language="bash" hasCopyButton container="card" />

        <Text size="sm" color="secondary">
          The script also has an in-process mode that loads the weights on your own machine and needs
          no account at all; it imports our inference wrapper, so it runs from a checkout of the
          service rather than from this file alone. We cross-checked the two paths on M4 Hourly and
          they agree: identical summary table at the precision quoted here, the same 20-of-100 loss
          rate against seasonal-naive, and a largest per-series divergence of 1.7e-4 sMAPE.
        </Text>

        <HStack gap={2} wrap="wrap">
          <Link href="/benchmarks/run.py" hasUnderline>
            run.py
          </Link>
          <Link href="/benchmarks/m4-300-summary.csv" hasUnderline>
            summary.csv
          </Link>
          <Link href="/benchmarks/m4-300-per-series.csv" hasUnderline>
            per-series.csv (900 rows)
          </Link>
        </HStack>

        <Text size="sm" color="secondary">
          The per-series file is the one to check first. Every row is one series, one method, one
          score — which is what you need to see the distribution rather than trust a mean.
        </Text>
      </VStack>
    </VStack>
  );
}
